import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  buildCatalogRows,
  filterCatalog,
  sortCatalog,
  type CatalogRow,
} from '@/lib/catalog';
import {
  decodePriceFragment,
  decodeShareQuery,
} from '@/lib/shareState';
import {
  defaultPrefs,
  loadBaselineId,
  loadPrefs,
  loadPriceOverrides,
  resetAllPriceOverrides,
  resetPriceOverride,
  saveBaselineId,
  savePrefs,
  savePriceOverride,
  type UserPrefs,
} from '@/lib/storage';
import type {
  CatalogDataset,
  CatalogFilters,
  DrinkCategory,
  PriceOverride,
  SortDirection,
  SortKey,
} from '@/types/data';
import { catalogContentHash } from '@/generated/catalogMeta';

interface CatalogContextValue {
  loading: boolean;
  error: string | null;
  dataset: CatalogDataset | null;
  rows: CatalogRow[];
  filteredSorted: CatalogRow[];
  prefs: UserPrefs;
  filters: CatalogFilters;
  baselineId: string | null;
  baselineRow: CatalogRow | null;
  overrides: Record<string, PriceOverride>;
  announcement: string;
  setSort: (key: SortKey, direction?: SortDirection) => void;
  setFilters: (patch: Partial<CatalogFilters>) => void;
  clearFilters: (preserveCategory?: DrinkCategory) => void;
  setBaseline: (productId: string | null) => void;
  setVolumeUnit: (unit: 'ml' | 'fl_oz') => void;
  setBaselineScope: (scope: 'subtype' | 'category' | 'all') => void;
  saveOverride: (override: PriceOverride) => void;
  clearOverride: (productId: string) => void;
  clearAllOverrides: () => void;
  getRowBySlug: (slug: string) => CatalogRow | undefined;
  getRowById: (id: string) => CatalogRow | undefined;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

const defaultFilters: CatalogFilters = {
  category: 'all',
  subtype: null,
  query: '',
  abvMin: null,
  abvMax: null,
  caloriesMax: null,
  carbsMax: null,
  verifiedNutritionOnly: false,
  pricedOnly: false,
  priceProvenance: 'all',
  includeZeroAbv: false,
};

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [dataset, setDataset] = useState<CatalogDataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<UserPrefs>(defaultPrefs);
  const [filters, setFiltersState] = useState<CatalogFilters>(defaultFilters);
  const [baselineId, setBaselineIdState] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<Record<string, PriceOverride>>({});
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    const storedPrefs = loadPrefs();
    setPrefs(storedPrefs);
    setFiltersState({ ...defaultFilters, ...storedPrefs.filters });
    setBaselineIdState(loadBaselineId());
    let nextOverrides = loadPriceOverrides();
    const shared = decodeShareQuery(window.location.search);
    if (shared.category) {
      setFiltersState((f) => ({ ...f, category: shared.category! }));
    }
    if (shared.subtype != null) {
      setFiltersState((f) => ({ ...f, subtype: shared.subtype ?? null }));
    }
    if (shared.query) {
      setFiltersState((f) => ({ ...f, query: shared.query! }));
    }
    if (shared.sortKey) {
      setPrefs((p) => ({
        ...p,
        sortKey: shared.sortKey!,
        sortDirection: shared.sortDirection ?? p.sortDirection,
      }));
    }
    if (shared.baselineId) setBaselineIdState(shared.baselineId);
    const frag = decodePriceFragment(window.location.hash);
    if (frag) nextOverrides = { ...nextOverrides, ...frag };
    setOverrides(nextOverrides);

    fetch(`/data/catalog.json?v=${catalogContentHash}`)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load catalog');
        return r.json();
      })
      .then((data: CatalogDataset) => setDataset(data))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const rows = useMemo(
    () => (dataset ? buildCatalogRows(dataset, overrides) : []),
    [dataset, overrides],
  );

  const filteredSorted = useMemo(
    () => sortCatalog(filterCatalog(rows, filters), prefs.sortKey, prefs.sortDirection),
    [rows, filters, prefs.sortKey, prefs.sortDirection],
  );

  const baselineRow = useMemo(
    () => rows.find((r) => r.product.id === baselineId) ?? null,
    [rows, baselineId],
  );

  const persistPrefs = useCallback((next: UserPrefs) => {
    setPrefs(next);
    savePrefs(next);
  }, []);

  const setSort = useCallback(
    (key: SortKey, direction?: SortDirection) => {
      setPrefs((prev) => {
        const nextDir =
          direction ??
          (prev.sortKey === key && prev.sortDirection === 'desc' ? 'asc' : 'desc');
        const next = { ...prev, sortKey: key, sortDirection: nextDir };
        savePrefs(next);
        setAnnouncement(
          `Sorted by ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}, ${nextDir === 'desc' ? 'high to low' : 'low to high'}`,
        );
        return next;
      });
    },
    [],
  );

  const setFilters = useCallback(
    (patch: Partial<CatalogFilters>) => {
      setFiltersState((prev) => {
        const next = { ...prev, ...patch };
        persistPrefs({ ...prefs, filters: next });
        return next;
      });
    },
    [persistPrefs, prefs],
  );

  const clearFilters = useCallback(
    (preserveCategory?: DrinkCategory) => {
      const nextFilters: CatalogFilters = {
        ...defaultFilters,
        category: preserveCategory ?? 'all',
      };
      setFiltersState(nextFilters);
      const nextPrefs: UserPrefs = {
        ...prefs,
        sortKey: 'alcoholPer100Calories',
        sortDirection: 'desc',
        volumeUnit: 'fl_oz',
        filters: nextFilters,
      };
      persistPrefs(nextPrefs);
      setAnnouncement('Filters cleared');
    },
    [persistPrefs, prefs],
  );

  const setBaseline = useCallback((productId: string | null) => {
    setBaselineIdState(productId);
    saveBaselineId(productId);
    setAnnouncement(
      productId ? 'Baseline drink set' : 'Baseline cleared — browsing all drinks',
    );
  }, []);

  const setVolumeUnit = useCallback(
    (unit: 'ml' | 'fl_oz') => {
      persistPrefs({ ...prefs, volumeUnit: unit });
    },
    [persistPrefs, prefs],
  );

  const setBaselineScope = useCallback(
    (scope: 'subtype' | 'category' | 'all') => {
      persistPrefs({ ...prefs, baselineScope: scope });
    },
    [persistPrefs, prefs],
  );

  const saveOverride = useCallback((override: PriceOverride) => {
    savePriceOverride(override);
    setOverrides(loadPriceOverrides());
    setAnnouncement(`Price updated for ${override.productId}. Results reranked.`);
  }, []);

  const clearOverride = useCallback((productId: string) => {
    resetPriceOverride(productId);
    setOverrides(loadPriceOverrides());
    setAnnouncement('Price override cleared');
  }, []);

  const clearAllOverrides = useCallback(() => {
    resetAllPriceOverrides();
    setOverrides({});
    setAnnouncement('All price overrides cleared');
  }, []);

  const getRowBySlug = useCallback(
    (slug: string) => rows.find((r) => r.product.slug === slug),
    [rows],
  );

  const getRowById = useCallback(
    (id: string) => rows.find((r) => r.product.id === id),
    [rows],
  );

  const value: CatalogContextValue = {
    loading,
    error,
    dataset,
    rows,
    filteredSorted,
    prefs,
    filters,
    baselineId,
    baselineRow,
    overrides,
    announcement,
    setSort,
    setFilters,
    clearFilters,
    setBaseline,
    setVolumeUnit,
    setBaselineScope,
    saveOverride,
    clearOverride,
    clearAllOverrides,
    getRowBySlug,
    getRowById,
  };

  return (
    <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
  );
}

export function useCatalog() {
  const ctx = useContext(CatalogContext);
  if (!ctx) throw new Error('useCatalog requires CatalogProvider');
  return ctx;
}

export function categoryFromPath(
  category?: DrinkCategory,
): DrinkCategory | 'all' {
  return category ?? 'all';
}
